// T064: Upgrade System (升級系統)
// 處理單位升級機制

import type { GameState, Card, CardDefinition, BattlefieldPosition } from '../../types';
import { getLogger } from '../../utils/logger';

const log = getLogger('UpgradeSystem');

/**
 * 升級條件類型
 */
export type UpgradeConditionType =
  | 'combatSuccess'      // 戰鬥成功
  | 'combatSuccessBackRow' // 戰鬥成功且目標在後排
  | 'atkAdvantage'       // 攻擊力優勢
  | 'effectDestroy'      // 效果破壞
  | 'unitCount'          // 場上單位數量
  | 'phaseStart';        // 階段開始時

/**
 * 升級規則
 */
export interface UpgradeRule {
  /** 基礎單位 ID（或名稱） */
  baseUnit: string;
  /** 升級後單位 ID */
  targetUnit: string;
  /** 觸發條件 */
  condition: UpgradeConditionType;
  /** 額外條件參數 */
  params?: {
    atkAdvantage?: number;     // 攻擊力優勢要求
    targetRow?: 'back';        // 目標位置要求
    requiredUnit?: string;     // 場上需要的單位
    unitCount?: number;        // 單位數量要求
    unitType?: string;         // 單位類型要求
  };
}

/**
 * 升級結果
 */
export interface UpgradeResult {
  success: boolean;
  error?: 'CONDITION_NOT_MET' | 'TARGET_NOT_FOUND' | 'ALREADY_UPGRADED';
  upgradedCard?: Card;
  newState?: GameState;
}

/**
 * 預設升級規則（從官方效果文字分析）
 */
const DEFAULT_UPGRADE_RULES: UpgradeRule[] = [
  // 見習弓箭手 → 雙弓勇者
  {
    baseUnit: '見習弓箭手',
    targetUnit: 'hero_brv0028',
    condition: 'combatSuccessBackRow',
    params: { targetRow: 'back' },
  },
  // 見習劍士 → 大劍勇者
  {
    baseUnit: '見習劍士',
    targetUnit: 'hero_brv0029',
    condition: 'atkAdvantage',
    params: { atkAdvantage: 3 },
  },
  // 見習魔法師 → 巫師勇者
  {
    baseUnit: '見習魔法師',
    targetUnit: 'hero_brv0036',
    condition: 'effectDestroy',
  },
  // 小旅人 → 菜鳥勇者（需要熱心勇者）
  {
    baseUnit: '小旅人',
    targetUnit: 'hero_brv0044',
    condition: 'combatSuccess',
    params: { requiredUnit: '熱心勇者' },
  },
  // 酒館闆娘 → 忙碌的酒館闆娘
  {
    baseUnit: '酒館闆娘',
    targetUnit: 'virtual_vrt0056',
    condition: 'unitCount',
    params: { unitCount: 3, unitType: '酒客' },
  },
];

// 升級規則註冊表
let upgradeRules: UpgradeRule[] = [...DEFAULT_UPGRADE_RULES];

/**
 * 註冊升級規則
 */
export function registerUpgradeRule(rule: UpgradeRule): void {
  upgradeRules.push(rule);
}

/**
 * 清除所有升級規則（測試用）
 */
export function clearUpgradeRules(): void {
  upgradeRules = [];
}

/**
 * 重置為預設規則
 */
export function resetUpgradeRules(): void {
  upgradeRules = [...DEFAULT_UPGRADE_RULES];
}

/**
 * 取得單位的可能升級
 */
export function getAvailableUpgrades(cardName: string): UpgradeRule[] {
  return upgradeRules.filter(rule => 
    rule.baseUnit === cardName || 
    cardName.includes(rule.baseUnit)
  );
}

/**
 * 檢查升級條件
 */
export function checkUpgradeCondition(
  state: GameState,
  card: Card,
  rule: UpgradeRule,
  context: UpgradeContext
): boolean {
  switch (rule.condition) {
    case 'combatSuccess':
      if (!context.combatWon) return false;
      // 檢查是否需要特定單位在場
      if (rule.params?.requiredUnit) {
        return hasUnitOnField(state, card.ownerId, rule.params.requiredUnit);
      }
      return true;

    case 'combatSuccessBackRow':
      return context.combatWon === true && 
             context.targetPosition?.row === 'back';

    case 'atkAdvantage':
      if (!context.combatWon || !context.atkDifference) return false;
      return context.atkDifference >= (rule.params?.atkAdvantage ?? 0);

    case 'effectDestroy':
      return context.effectDestroyed === true;

    case 'unitCount': {
      const unitCount = rule.params?.unitCount;
      const unitType = rule.params?.unitType;
      if (!unitCount || !unitType) return false;
      return countUnitsOfType(state, card.ownerId, unitType) >= unitCount;
    }

    case 'phaseStart':
      return context.phaseStart === true;

    default:
      return false;
  }
}

/**
 * 升級上下文（提供條件檢查所需資訊）
 */
export interface UpgradeContext {
  combatWon?: boolean;
  targetPosition?: BattlefieldPosition;
  atkDifference?: number;
  effectDestroyed?: boolean;
  phaseStart?: boolean;
}

/**
 * 執行升級
 */
export function upgradeUnit(
  state: GameState,
  cardId: string,
  targetDefinitionId: string,
  definitions: Map<string, CardDefinition>
): UpgradeResult {
  // 找到要升級的卡牌
  const cardInfo = findCardOnBattlefield(state, cardId);
  if (!cardInfo) {
    return { success: false, error: 'TARGET_NOT_FOUND' };
  }

  const { card, position } = cardInfo;
  
  // 取得目標定義
  const targetDef = definitions.get(targetDefinitionId);
  if (!targetDef) {
    return { success: false, error: 'TARGET_NOT_FOUND' };
  }

  // 建立升級後的卡牌
  const upgradedCard: Card = {
    ...card,
    definitionId: targetDefinitionId,
    // 保留攻擊力修正等狀態
    atkModifier: card.atkModifier,
    scoreModifier: card.scoreModifier,
    activeEffects: targetDef.effectIds ?? [],
  };

  // 更新狀態
  const newState = replaceCardOnBattlefield(state, position, upgradedCard);

  log.debug(`${card.definitionId} 升級為 ${targetDefinitionId}`);

  return {
    success: true,
    upgradedCard,
    newState,
  };
}

/**
 * 嘗試自動升級（在符合條件時）
 */
export function tryAutoUpgrade(
  state: GameState,
  cardId: string,
  context: UpgradeContext,
  definitions: Map<string, CardDefinition>
): UpgradeResult {
  const cardInfo = findCardOnBattlefield(state, cardId);
  if (!cardInfo) {
    return { success: false, error: 'TARGET_NOT_FOUND' };
  }

  const { card } = cardInfo;
  const cardDef = definitions.get(card.definitionId);
  const cardName = cardDef?.nameZh ?? cardDef?.name ?? '';

  // 找到適用的升級規則
  const rules = getAvailableUpgrades(cardName);
  
  for (const rule of rules) {
    if (checkUpgradeCondition(state, card, rule, context)) {
      return upgradeUnit(state, cardId, rule.targetUnit, definitions);
    }
  }

  return { success: false, error: 'CONDITION_NOT_MET' };
}

// === 輔助函數 ===

function findCardOnBattlefield(
  state: GameState,
  cardId: string
): { card: Card; position: BattlefieldPosition } | null {
  const sides: Array<{ key: 'player1' | 'player2'; side: typeof state.battlefield.player1Side }> = [
    { key: 'player1', side: state.battlefield.player1Side },
    { key: 'player2', side: state.battlefield.player2Side },
  ];

  for (const { key, side } of sides) {
    for (let i = 0; i < side.frontLine.length; i++) {
      const unit = side.frontLine[i].unit;
      if (unit?.id === cardId) {
        return {
          card: unit,
          position: { side: key, row: 'front', column: (i + 1) as 1 | 2 | 3 | 4 | 5 },
        };
      }
    }
    for (let i = 0; i < side.backLine.length; i++) {
      const unit = side.backLine[i].unit;
      if (unit?.id === cardId) {
        return {
          card: unit,
          position: { side: key, row: 'back', column: (i + 1) as 1 | 2 | 3 | 4 | 5 },
        };
      }
    }
  }

  return null;
}

function replaceCardOnBattlefield(
  state: GameState,
  position: BattlefieldPosition,
  newCard: Card
): GameState {
  const sideKey = position.side === 'player1' ? 'player1Side' : 'player2Side';
  const lineKey = position.row === 'front' ? 'frontLine' : 'backLine';

  const newBattlefield = { ...state.battlefield };
  const newSide = { ...newBattlefield[sideKey] };
  const newLine = [...newSide[lineKey]];

  newLine[position.column - 1] = {
    ...newLine[position.column - 1],
    unit: { ...newCard, position },
  };

  newSide[lineKey] = newLine;
  newBattlefield[sideKey] = newSide;

  return { ...state, battlefield: newBattlefield };
}

function hasUnitOnField(state: GameState, playerId: string, unitName: string): boolean {
  const side = playerId === state.player1.id
    ? state.battlefield.player1Side
    : state.battlefield.player2Side;

  for (const slot of [...side.frontLine, ...side.backLine]) {
    if (slot.unit) {
      // 簡單檢查名稱（實際應該查 definition）
      if (slot.unit.definitionId.includes(unitName.toLowerCase().replace(/\s/g, ''))) {
        return true;
      }
    }
  }

  return false;
}

function countUnitsOfType(state: GameState, playerId: string, unitType: string): number {
  const side = playerId === state.player1.id
    ? state.battlefield.player1Side
    : state.battlefield.player2Side;

  let count = 0;
  for (const slot of [...side.frontLine, ...side.backLine]) {
    if (slot.unit) {
      // 簡單檢查類型（實際應該查 definition 的 tag）
      if (slot.unit.definitionId.toLowerCase().includes(unitType.toLowerCase())) {
        count++;
      }
    }
  }

  return count;
}

// 匯出為物件
export const UpgradeSystem = {
  registerUpgradeRule,
  clearUpgradeRules,
  resetUpgradeRules,
  getAvailableUpgrades,
  checkUpgradeCondition,
  upgradeUnit,
  tryAutoUpgrade,
};
